"use client";

import { lazy, Suspense, type ReactNode, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AppProvider } from "./context/AppContext";
import { AiPage } from "./screens/AiPage";
import { FinancePage } from "./screens/FinancePage";
import { GrowthPage } from "./screens/GrowthPage";
import { LearningPage } from "./screens/LearningPage";
import { PlansPage } from "./screens/PlansPage";
import { EnglishPage } from "./screens/EnglishPage";
import { WorksPage } from "./screens/WorksPage";
import { LoginPage } from "./screens/LoginPage";
import { ProjectsPage } from "./screens/ProjectsPage";
import { ReviewsPage } from "./screens/ReviewsPage";
import { SettingsPage } from "./screens/SettingsPage";
import { TimelinePage } from "./screens/TimelinePage";
import { TodayPage } from "./screens/TodayPage";
import { WorkPage } from "./screens/WorkPage";

const WorkbenchPage = lazy(() => import("./screens/WorkbenchPage").then((module) => ({ default: module.WorkbenchPage })));

const UiToolkitPrototypePage = import.meta.env.DEV
  ? lazy(() =>
      import("./screens/UiToolkitPrototypePage").then((module) => ({ default: module.UiToolkitPrototypePage }))
    )
  : null;

function isBrowserTrial() {
  return typeof window !== "undefined" && globalThis.__EARTH_ONLINE_BROWSER_TRIAL__ === true;
}

function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  let hasToken = false;
  try {
    hasToken = Boolean(localStorage.getItem("eo_token"));
  } catch {
    /* ignore */
  }
  if (!hasToken && !isBrowserTrial()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

export default function App() {
  useEffect(() => {
    try {
      const saved = localStorage.getItem("eo_theme");
      if (saved) document.documentElement.setAttribute("data-theme", saved);
    } catch {
      /* ignore */
    }
  }, []);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/work/desk/*" element={<AuthGate><Suspense fallback={<div role="status">正在打开业务工作台…</div>}><WorkbenchPage /></Suspense></AuthGate>} />
        {UiToolkitPrototypePage && (
          <Route
            path="/prototype/ui-toolkit"
            element={
              <AppProvider>
                <AppShell>
                  <Suspense fallback={null}>
                    <UiToolkitPrototypePage />
                  </Suspense>
                </AppShell>
              </AppProvider>
            }
          />
        )}
        <Route
          path="/*"
          element={
            <AuthGate>
              <AppProvider>
                <AppShell>
                  <Routes>
                    <Route path="/" element={<Navigate to="/today" replace />} />
                    <Route path="/today" element={<TodayPage />} />
                    <Route path="/timeline" element={<TimelinePage />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/work" element={<WorkPage />} />
                    <Route path="/learning" element={<LearningPage />} />
                    <Route path="/plans" element={<PlansPage />} />
                    <Route path="/english" element={<EnglishPage />} />
                    <Route path="/works" element={<WorksPage />} />
                    <Route path="/growth" element={<GrowthPage />} />
                    <Route path="/finance" element={<FinancePage />} />
                    <Route path="/reviews" element={<ReviewsPage />} />
                    <Route path="/ai" element={<AiPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/today" replace />} />
                  </Routes>
                </AppShell>
              </AppProvider>
            </AuthGate>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
